"use client";

import React, { useState } from "react";
import { useTranslations, useLocale } from "next-intl";
import { useFormContext, Controller } from "react-hook-form";
import GeneratorPage from "../GeneratorPage";
import { useLocaleNavigation } from '@/lib/navigation';
import { CATEGORIES } from "@/constants/categories";
import { Skull, Copy, Check } from "lucide-react";
import { Input, Select, Button } from "@/components/design-system";
import { generateHorrorNames } from "@/lib/horrorNameGenerator";

export default function HorrorNamesGenerator() {
    const t = useTranslations("horror-names");
    const locale = useLocale();
    const [generated, setGenerated] = useState<string[]>([]);
    const [isGenerating, setIsGenerating] = useState(false);
    const [copied, setCopied] = useState(false);

    const supportedLocales = ['en', 'es', 'fr', 'de', 'pt', 'tr', 'ar', 'hi', 'ja', 'ko', 'id', 'vi', 'ru', 'it', 'pl', 'nl'];
    const defaultLanguage = supportedLocales.includes(locale) ? locale : 'en';

    // Initial default values
    const defaultValues = {
        count: 10,
        style: 'any',
        language: defaultLanguage
    };

    const handleGenerate = (e: React.FormEvent, data?: any) => {
        if (e && e.preventDefault) e.preventDefault();
        setIsGenerating(true);

        // Use data from React Hook Form or fallback to defaults
        const formData = data || defaultValues;

        // Simulate network delay for better UX
        setTimeout(() => {
            const names = generateHorrorNames({
                count: Number(formData.count),
                style: formData.style,
                locale: formData.language
            });
            setGenerated(names);
            setIsGenerating(false);
        }, 500);
    };

    const handleCopyAll = async () => {
        await navigator.clipboard.writeText(generated.join("\n"));
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
    };

    const formConfig = {
        values: defaultValues,
        onChange: () => { }, // Handled by react-hook-form internally in GeneratorPage
        fields: [
            {
                key: "style",
                label: t("form.style"),
                type: "select" as const,
                options: [
                    { value: "any", label: t("form.styleOptions.any") },
                    { value: "demon", label: t("form.styleOptions.demon") },
                    { value: "slasher", label: t("form.styleOptions.slasher") },
                    { value: "cosmic", label: t("form.styleOptions.cosmic") },
                ],
                defaultValue: "any",
            },
            {
                key: "language",
                label: t("form.language"),
                type: "select" as const,
                options: [
                    { value: "en", label: t("form.languageOptions.en") },
                    { value: "es", label: t("form.languageOptions.es") },
                    { value: "fr", label: t("form.languageOptions.fr") },
                    { value: "de", label: t("form.languageOptions.de") },
                    { value: "pt", label: t("form.languageOptions.pt") },
                    { value: "tr", label: t("form.languageOptions.tr") },
                    { value: "ar", label: t("form.languageOptions.ar") },
                    { value: "hi", label: t("form.languageOptions.hi") },
                    { value: "ja", label: t("form.languageOptions.ja") },
                    { value: "ko", label: t("form.languageOptions.ko") },
                    { value: "id", label: t("form.languageOptions.id") },
                    { value: "vi", label: t("form.languageOptions.vi") },
                    { value: "ru", label: t("form.languageOptions.ru") },
                    { value: "it", label: t("form.languageOptions.it") },
                    { value: "pl", label: t("form.languageOptions.pl") },
                    { value: "nl", label: t("form.languageOptions.nl") },
                ],
                defaultValue: defaultValues.language,
            },
            {
                key: "count",
                label: t("form.count"),
                type: "number" as const,
                placeholder: t("form.countPlaceholder"),
                validation: {
                    min: 1,
                    max: 50,
                },
                defaultValue: 10,
            },
        ],
    };

    const customResultsRenderer = () => {
        if (!generated || generated.length === 0) {
            return (
                <div className="text-center text-gray-500 py-8">
                    {t("results.noResults")}
                </div>
            );
        }

        return (
            <div className="space-y-6">
                <div className="flex items-center justify-between">
                    <h3 className="text-lg font-semibold">{t("results.title")}</h3>
                    <Button type="button" size="sm" variant="outline" onClick={handleCopyAll}>
                        {copied ? (
                            <>
                                <Check className="mr-2 h-4 w-4" />
                                {t("results.copyAllSuccess")}
                            </>
                        ) : (
                            <>
                                <Copy className="mr-2 h-4 w-4" />
                                {t("results.copyAll")}
                            </>
                        )}
                    </Button>
                </div>
                <div className="space-y-2">
                    {generated.map((name, index) => (
                        <div key={index} className="flex items-center justify-between p-3 bg-white rounded-lg border border-gray-200 hover:border-red-500 transition-colors group">
                            <span className="font-medium text-gray-900">{name}</span>
                            <button
                                onClick={() => {
                                    navigator.clipboard.writeText(name);
                                }}
                                className="p-2 text-gray-400 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity"
                                title={t("form.copyButton")}
                            >
                                <Copy className="h-4 w-4" />
                            </button>
                        </div>
                    ))}
                </div>
            </div>
        );
    };

    // Form Content Component to access context
    const FormContent = () => {
        const { control } = useFormContext();
        return (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                    <label className="block mb-1 font-medium">{t("form.style")}</label>
                    <Controller
                        name="style"
                        control={control}
                        render={({ field }) => (
                            <Select
                                value={field.value}
                                onChange={(e) => field.onChange(e.target.value)}
                            >
                                <option value="any">{t("form.styleOptions.any")}</option>
                                <option value="demon">{t("form.styleOptions.demon")}</option>
                                <option value="slasher">{t("form.styleOptions.slasher")}</option>
                                <option value="cosmic">{t("form.styleOptions.cosmic")}</option>
                            </Select>
                        )}
                    />
                </div>
                <div>
                    <label className="block mb-1 font-medium">{t("form.language")}</label>
                    <Controller
                        name="language"
                        control={control}
                        render={({ field }) => (
                            <Select
                                value={field.value}
                                onChange={(e) => field.onChange(e.target.value)}
                            >
                                <option value="en">{t("form.languageOptions.en")}</option>
                                <option value="es">{t("form.languageOptions.es")}</option>
                                <option value="fr">{t("form.languageOptions.fr")}</option>
                                <option value="de">{t("form.languageOptions.de")}</option>
                                <option value="pt">{t("form.languageOptions.pt")}</option>
                                <option value="tr">{t("form.languageOptions.tr")}</option>
                                <option value="ar">{t("form.languageOptions.ar")}</option>
                                <option value="hi">{t("form.languageOptions.hi")}</option>
                                <option value="ja">{t("form.languageOptions.ja")}</option>
                                <option value="ko">{t("form.languageOptions.ko")}</option>
                                <option value="id">{t("form.languageOptions.id")}</option>
                                <option value="vi">{t("form.languageOptions.vi")}</option>
                                <option value="ru">{t("form.languageOptions.ru")}</option>
                                <option value="it">{t("form.languageOptions.it")}</option>
                                <option value="pl">{t("form.languageOptions.pl")}</option>
                                <option value="nl">{t("form.languageOptions.nl")}</option>
                            </Select>
                        )}
                    />
                </div>
                <div>
                    <label className="block mb-1 font-medium">{t("form.count")}</label>
                    <Controller
                        name="count"
                        control={control}
                        render={({ field }) => (
                            <Input
                                type="number"
                                min={1}
                                max={50}
                                value={field.value}
                                onChange={(e) => field.onChange(Number(e.target.value))}
                                placeholder={t("form.countPlaceholder")}
                            />
                        )}
                    />
                </div>
            </div>
        );
    };

    const { getNavigationPath } = useLocaleNavigation();
    // locale used to be defined here, moved up

    // Calculate similar generators
    const category = CATEGORIES.entertainment;
    const similarGenerators = category.generators
        .filter(g => g.id !== 'horror-names')
        .slice(0, 3) // Show first 3 alternatives
        .map(g => {
            const nameKey = `similarHelpers.${g.id}.name` as any;
            const descKey = `similarHelpers.${g.id}.description` as any;

            // Use t.has() to check if the key exists before translating
            const name = t.has(nameKey) ? t(nameKey) : (g.name[locale as keyof typeof g.name] || g.name['en']);
            const description = t.has(descKey) ? t(descKey) : (g.description[locale as keyof typeof g.description] || g.description['en']);

            return {
                id: g.id,
                name,
                description,
                icon: g.icon,
                href: getNavigationPath(g.id),
                category: category.name['en']
            };
        });

    return (
        <GeneratorPage
            title={t("title")}
            description={t("description")}
            icon={<Skull className="h-4 w-4" />}
            category={CATEGORIES.entertainment}
            generatedData={generated.join("\n")}
            isGenerating={isGenerating}
            onGenerate={handleGenerate}
            formConfig={formConfig}
            resultsRenderer={customResultsRenderer()}
            generateButtonText={t("form.generateButton")}
            faqs={t.raw("faqs")}
            similarGenerators={similarGenerators}
        >
            <FormContent />
        </GeneratorPage>
    );
}
